import * as Api from '@ViewModels';
import { ICustomSurvey, IRichContentEditorState } from '@ViewModels';
import * as yup from 'yup';
import {
	buildSurveyPartsSchema,
	createBaseSurveySchema,
	createCustomFormFieldSchema,
	createNotificationSettingsSchema,
	createTagGenerationSettingsSchema,
} from '../shared/validation/schemas';
import { isV2SurveyForm } from '../shared/components/QuestionsWithSections/utils/transform';

// Form type that allows IRichContentEditorState for form fields but matches the expected structure
export type CustomSurveyFormData = Omit<Partial<ICustomSurvey>, 'options'> & {
	updateExistingContacts?: boolean;
	options?: {
		contactNameOptions?: Api.IContactFieldOptions;
		contactEmailOptions?: Api.IContactFieldOptions;
		contactPhoneNumberOptions?: Api.IContactFieldOptions;
		contactBirthdayOptions?: Api.IContactFieldOptions;
		contactAddressOptions?: Api.IContactFieldOptions;
		intro?: IRichContentEditorState;
		companyName?: string;
		sendConfirmationMessage?: boolean;
		includeCompanyLogo?: boolean;
		showSurveyName?: boolean;
		confirmationMessage?: IRichContentEditorState;
	};
	tagGenerationSettings?: {
		tagOnCompletion?: boolean;
		completionTag?: string;
		customFormFieldTagSettings?: {
			fieldId: string;
			shouldGenerateTag: boolean;
			tagPrefix: string;
		}[];
		effectiveCompletionTag?: Api.IAccountTag;
	};
	surveyFormVisibilityValidation?: {
		enabled: boolean;
		hasQuestion: boolean;
		hasAllAnswers: boolean;
	}[];
};

export const validationSchema = yup.object({
	// Base survey fields using shared schema (CustomSurvey doesn't require intro/dates)
	...createBaseSurveySchema({
		requireIntro: false,
		requireStartDate: false,
		requireExpirationDate: false,
	}),

	// CustomSurvey-specific options
	options: yup.object({
		contactNameOptions: yup
			.object({
				collect: yup.boolean(),
				require: yup.boolean(),
				updateExisting: yup.boolean(),
			})
			.optional(),
		contactEmailOptions: yup
			.object({
				collect: yup.boolean(),
				require: yup.boolean(),
				updateExisting: yup.boolean(),
			})
			.optional(),
		contactPhoneNumberOptions: yup
			.object({
				collect: yup.boolean(),
				require: yup.boolean(),
				updateExisting: yup.boolean(),
			})
			.optional(),
		contactBirthdayOptions: yup
			.object({
				collect: yup.boolean(),
				require: yup.boolean(),
				updateExisting: yup.boolean(),
			})
			.optional(),
		contactAddressOptions: yup
			.object({
				collect: yup.boolean(),
				require: yup.boolean(),
				updateExisting: yup.boolean(),
			})
			.optional(),
		sendConfirmationMessage: yup.boolean().optional().default(false),
		intro: yup.mixed<IRichContentEditorState>().optional(),
		companyName: yup.string().optional(),
		includeCompanyLogo: yup.boolean().optional().default(false),
		showSurveyName: yup.boolean().optional().default(true),
		image: yup.string().optional(),
		confirmationMessage: yup.mixed<IRichContentEditorState>().optional(),
	}),

	// Custom form with CustomSurvey-specific requirements.
	//
	// Wrapped in yup.lazy so the customForm.fields label rule can read the sibling
	// surveyForm to decide V2 vs V1. V2 (sectioned) surveys store question labels in
	// surveyForm.surveyParts (validated via the surveyForm gate below); customForm.fields
	// is a derived mirror rebuilt from surveyParts only at save time, so during V2 editing
	// its labels are stale/empty. Requiring them throws a false "Question label is required"
	// that blocks save — so skip the mirror's label validation entirely for V2. V1 still
	// enforces it.
	//
	// The lazy resolver's second arg exposes `parent` (the root form value) — a top-level
	// lazy schema is rejected by yupResolver's types, so gating happens here at the field.
	customForm: yup.lazy((_value, options) => {
		const root = (options as { parent?: { surveyForm?: Parameters<typeof isV2SurveyForm>[0] } })?.parent;
		const isV2 = isV2SurveyForm(root?.surveyForm);
		return yup.object({
			name: yup.string(),
			startDate: yup.date(),
			fields: createCustomFormFieldSchema({
				requireLabels: true, // CustomSurvey allows flexible label requirements
				skipLabelValidation: isV2,
			}),
		});
	}),

	// Shared notification settings (no ratings for CustomSurvey)
	notificationSettings: createNotificationSettingsSchema().optional(),

	// Shared tag generation settings
	tagGenerationSettings: createTagGenerationSettingsSchema().optional(),

	// CustomSurvey-specific fields
	hasDateRange: yup.boolean().optional().default(false),
	allowPartialSubmission: yup.boolean().optional().default(false),

	// SurveyForm — V2 path validates surveyParts; V1 path falls through as mixed.
	// V1 surveys populate surveyForm.surveyParts with reference-only objects
	// (`{ id, _type, fieldId, visibleWhen }` — no `label`/`options`) for visibility
	// tracking only; V1's actual validation lives in createCustomFormFieldSchema
	// over `customForm.fields`. Applying the V2 schema unconditionally renders
	// every V1 survey unsaveable, so gate on shape.
	surveyForm: yup.lazy(value =>
		isV2SurveyForm(value as Parameters<typeof isV2SurveyForm>[0])
			? yup
					.object({
						isEnabled: yup.boolean().optional(),
						surveyParts: buildSurveyPartsSchema({ requireLabels: true }).optional(),
					})
					.optional()
			: yup.mixed().optional()
	),

	// Per-field conditional visibility validation entries
	// Parallel array matching customForm.fields, used to validate incomplete visibility configs
	surveyFormVisibilityValidation: yup
		.array()
		.of(
			yup
				.object({
					enabled: yup.boolean(),
					hasQuestion: yup.boolean(),
					hasAllAnswers: yup.boolean(),
				})
				.test(
					'conditional-visibility-complete',
					'Please select a question and answer for conditional visibility',
					function (value) {
						if (!value?.enabled) return true;
						if (!value.hasQuestion) {
							return this.createError({ message: 'Please select a question for conditional visibility' });
						}
						if (!value.hasAllAnswers) {
							return this.createError({ message: 'Please select an answer for each conditional visibility row' });
						}
						return true;
					}
				)
		)
		.optional(),
});

export const TabValues = {
	Questions: 'Questions',
	Settings: 'Settings',
	Preview: 'Preview',
} as const;

export type TabValues = (typeof TabValues)[keyof typeof TabValues];
